import { z } from 'zod';

// Branding schema (docs/09, Fleet Guy pattern) — one Zod schema serving the
// backend validator and the frontend branding editor form. Lives in the
// concept `branding` JSONB, so new fields are additive with NO migration.

const hexColour = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'invalid_hex_colour');
const contrastChoice = z.enum(['auto', 'light', 'dark']);

// Per-concept typography (licence-free Google Fonts). Real brand faces
// (field-gothic / Gotham) are licensed Adobe Typekit — slot a kit in later
// by extending this registry; nothing else changes.
export const FONT_REGISTRY = {
  'public-sans': {
    family: 'Public Sans',
    stack: "'Public Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
    url: 'https://fonts.googleapis.com/css2?family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap',
  },
  // Autofy UI face — they literally use Montserrat on the live site.
  montserrat: {
    family: 'Montserrat',
    stack: "'Montserrat', system-ui, -apple-system, 'Segoe UI', sans-serif",
    url: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap',
  },
  // Autofy display face — heavy condensed, stands in for field-gothic.
  oswald: {
    family: 'Oswald',
    stack: "'Oswald', 'Arial Narrow', system-ui, sans-serif",
    url: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap',
  },
  // 1TEC face — geometric grotesque, stands in for Gotham.
  jost: {
    family: 'Jost',
    stack: "'Jost', system-ui, -apple-system, 'Segoe UI', sans-serif",
    url: 'https://fonts.googleapis.com/css2?family=Jost:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap',
  },
} as const;

export const FONT_KEYS = Object.keys(FONT_REGISTRY) as [keyof typeof FONT_REGISTRY, ...(keyof typeof FONT_REGISTRY)[]];
export type FontKey = keyof typeof FONT_REGISTRY;
const fontChoice = z.enum(FONT_KEYS);

export const brandingSchema = z.object({
  primary_color: hexColour,
  secondary_color: hexColour,
  accent_color: hexColour,
  button_color: hexColour.nullable().default(null),
  sidebar_text_color: z.enum(['light', 'dark']).default('light'),
  button_text_contrast: contrastChoice.default('auto'),
  active_nav_text_contrast: contrastChoice.default('auto'),
  message_text_contrast: contrastChoice.default('auto'),
  logo_alt_text: z.string().max(100).nullable().default(null),
  // --- typography + auth presentation (all optional, default-safe) ---
  display_font: fontChoice.default('public-sans'),
  body_font: fontChoice.default('public-sans'),
  /** Pre-auth layout: full split-screen brand panel, or the simple centred card. */
  auth_layout: z.enum(['split', 'centered']).default('split'),
  /** Decorative motif on the brand panel — angular chevrons, soft glow, or none. */
  auth_motif: z.enum(['angular', 'glow', 'none']).default('glow'),
  /** Headline shown on the brand panel (the concept's tagline). */
  auth_tagline: z.string().max(120).nullable().default(null),
  /** Supporting paragraph under the headline on the brand panel. */
  auth_subtext: z.string().max(240).nullable().default(null),
  /** Hero image behind the brand-gradient overlay (root-relative URL, e.g. /brand/lkq-hero.jpg). */
  auth_hero: z.string().max(200).nullable().default(null),
  /** CSS gradient layer(s) painted OVER the hero (everything before the url()). Lets each
   *  concept's login match its design exactly; falls back to a generic brand ramp if null. */
  auth_overlay: z.string().max(800).nullable().default(null),
  /** background-position for the hero (e.g. 'center', 'left center'). */
  auth_bg_position: z.string().max(40).nullable().default(null),
  /** Brand-panel (login) logo height in px (logos differ in proportion per brand). */
  auth_logo_height: z.number().int().min(12).max(72).nullable().default(null),
  /** Sidebar logo height in px (some brand marks read small at the 28px default). */
  sidebar_logo_height: z.number().int().min(12).max(52).nullable().default(null),
  /** Brand-panel headline px size + max-width, and subtext px size — designs differ per brand. */
  auth_headline_size: z.number().int().min(24).max(80).nullable().default(null),
  auth_headline_max_width: z.number().int().min(200).max(720).nullable().default(null),
  auth_subtext_size: z.number().int().min(12).max(24).nullable().default(null),
  /** Opacity (0–1) of the white subtext + footer over the hero (per-brand in the designs). */
  auth_subtext_opacity: z.number().min(0).max(1).nullable().default(null),
  auth_footer_opacity: z.number().min(0).max(1).nullable().default(null),
  enabled: z.boolean().default(false),
});

export type Branding = z.infer<typeof brandingSchema>;
export const brandingUpdateSchema = brandingSchema.partial();

/** Hardcoded end of the resolution chain (concept → platform default → this). */
export const FALLBACK_BRANDING: Branding = {
  primary_color: '#0043B8',
  secondary_color: '#001738',
  accent_color: '#27758E',
  button_color: null,
  sidebar_text_color: 'light',
  button_text_contrast: 'auto',
  active_nav_text_contrast: 'auto',
  message_text_contrast: 'auto',
  logo_alt_text: 'LKQ Europe',
  display_font: 'montserrat',
  body_font: 'public-sans',
  auth_layout: 'split',
  auth_motif: 'glow',
  auth_tagline: 'Run the network.',
  auth_subtext: 'Sign in to administer concepts, garages, branding, content and members across LKQ Europe.',
  auth_hero: '/brand/lkq-hero.jpg',
  auth_overlay:
    'linear-gradient(120deg, rgba(0,38,104,0.95) 0%, rgba(0,54,146,0.88) 46%, rgba(6,66,176,0.70) 100%)',
  auth_bg_position: 'center bottom',
  auth_logo_height: 26,
  sidebar_logo_height: 26,
  auth_headline_size: 44,
  auth_headline_max_width: 480,
  auth_subtext_size: 17,
  auth_subtext_opacity: 0.9,
  auth_footer_opacity: 0.72,
  enabled: true,
};

/** What the concept-config bootstrap returns (resolved chain + asset URLs). */
export interface ResolvedBranding extends Branding {
  logoUrl: string | null;
  faviconUrl: string | null;
  source: 'concept' | 'platform';
}

/**
 * Auto-contrast helper (docs/09): readable text colour for a brand colour.
 * WCAG relative-luminance heuristic; explicit overrides win where configured.
 */
export function readableTextOn(hex: string): 'light' | 'dark' {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return 'dark';
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.4 ? 'dark' : 'light';
}

// Terminology overrides (docs/09) — closed key set; free-form renaming of
// arbitrary UI is not allowed.
export const TERMINOLOGY_KEY_PATTERN = /^(benefits_label|news_label|events_label|downloads_label|role_label\.[a-z0-9_]+)$/;

export const terminologySchema = z
  .record(z.string().regex(TERMINOLOGY_KEY_PATTERN, 'invalid_terminology_key'), z.string().min(1).max(40))
  .refine((obj) => Object.keys(obj).length <= 30, 'too_many_terminology_keys');

export type Terminology = z.infer<typeof terminologySchema>;
